import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { prop, ifProp } from 'styled-tools';
import { Form, PopoverBody, Row } from 'reactstrap';
import LargestCol from 'components/LargestCol';
import Button from 'components/Button';
import Input from 'components/Input';
import Label from 'components/Input/Label';
import PropTypes from 'prop-types';
import StyledPopover from 'components/StyledPopover';
import FormattedDate from 'components/FormattedDate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isHostBookingSource } from 'utils/routeUtils';
import { blockShape } from 'utils/shapes';
import UserAccountSearch from 'routes/DateSelection/components/UserAccountSearch';

const StyledOffersForm = styled.div`
  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    padding: 1rem 0.9375rem;
  }

  .align-items-end {
    margin-bottom: 0.75rem;
  }
`;

const StyledLabel = styled(Label)`
  ${ifProp(
    'isHostBooking',
    css`
      @media screen and (max-width: ${prop('theme.breakpoints.extraLarge')}) {
        text-transform: capitalize;
        font-size: 0.65em;
      }
    `
  )}
`;

const StyledButton = styled(Button)`
  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    margin-top: 0.5rem;
  }
`;

class OffersForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      accountNumberInvalid: false,
      accountPinInvalid: false,
      showValidationErrors: false
    };
  }

  handleClick = e => {
    e.preventDefault();

    const {
      accountNumberInvalid,
      accountPinInvalid,
      secondAccountNumberInvalid
    } = this.state;

    const {
      submitRewardsAccount,
      accountNumber,
      accountPin,
      secondAccountNumber,
      activeBlock,
      resetBlockCode,
      submitBlockCode
    } = this.props;

    if (
      accountNumberInvalid ||
      accountPinInvalid ||
      secondAccountNumberInvalid
    ) {
      this.setState({ showValidationErrors: true });

      return;
    }

    this.setState({ showValidationErrors: false });

    if (activeBlock) {
      resetBlockCode(e);
    } else {
      submitBlockCode(e);
    }

    submitRewardsAccount(accountNumber, accountPin, secondAccountNumber);
  };

  hideValidationErrors = () => {
    this.setState({ showValidationErrors: false });
  };

  handleAccountNumberChanged = e => {
    const { setAccountNumber } = this.props;
    const value = e.target.value.trim();
    const matches = value.match(/^\d+$/g);

    setAccountNumber(e.target.value.trim());

    this.setState({
      accountNumberInvalid:
        value.length > 0 &&
        (!(matches && matches.length > 0) || value.length !== 13)
    });
  };

  handleSecondAccountNumberChanged = e => {
    const { setSecondAccountNumber } = this.props;
    const value = e.target.value.trim();
    const matches = value.match(/^\d+$/g);

    setSecondAccountNumber(e.target.value.trim());

    this.setState({
      secondAccountNumberInvalid:
        value.length > 0 &&
        (!(matches && matches.length > 0) || value.length !== 13)
    });
  };

  handlePinChanged = e => {
    const { setAccountPin } = this.props;

    const value = e.target.value
      .trim()
      .replace(/[^\d]/g, '')
      .substr(0, 4);
    const matches = value.match(/^\d+$/g);

    setAccountPin(value);

    this.setState({
      accountPinInvalid:
        value.length > 0 &&
        (!(matches && matches.length > 0) || value.length !== 4)
    });
  };

  handleUserSelected = ({ patronAccountNumber }) => {
    this.handleAccountNumberChanged({ target: { value: patronAccountNumber } });
  };

  handleSecondUserSelected = ({ patronAccountNumber }) => {
    this.handleSecondAccountNumberChanged({
      target: { value: patronAccountNumber }
    });
  };

  render() {
    const {
      accountNumber,
      secondAccountNumber,
      accountPin,
      loading,
      disabled,
      isAdmin,
      token,
      bookingSource,
      activeBlock,
      submittedBlockCode,
      patronOrGroupCodeError,
      blockCode,
      setBlockCode,
      inMobileApp
    } = this.props;

    const {
      accountNumberInvalid,
      accountPinInvalid,
      showValidationErrors
    } = this.state;

    const isHostBooking = isHostBookingSource(bookingSource);

    return (
      <React.Fragment>
        <StyledOffersForm>
          {isHostBooking && !inMobileApp && (
            <UserAccountSearch
              onUserSelect={this.handleUserSelected}
              onSecondUserSelect={this.handleSecondUserSelected}
            />
          )}
          {!isHostBooking && (
            <>
              <h5>Find Special Rates and Offers!</h5>
              <p className="rewards-note">
                Enter your rewards number and PIN to see availability
              </p>
            </>
          )}
          <Form autoComplete="new-password">
            {/* TODO: Uncomment when block code work is done */}
            {/* {activeBlock && activeBlock.code && (
              <Row>
                <LargestCol xs="12">
                  {' '}
                  <small>
                    <sup>*</sup> - Your group code{' '}
                    <strong>{activeBlock.code}</strong> has been applied. Valid{' '}
                    <FormattedDate value={activeBlock.startDate} />
                    -
                    <FormattedDate value={activeBlock.endDate} />
                  </small>
                </LargestCol>
              </Row>
            )} */}
            <Row className="align-items-end">
              {/* TODO: Uncomment when block code work is done */}
              {/* <LargestCol xs="12" lg="2">
                <StyledLabel>
                  Group Code
                  {submittedBlockCode && patronOrGroupCodeError && !loading && (
                    <React.Fragment>
                      {' '}
                      <small>
                        <sup>*</sup> - {patronOrGroupCodeError}
                      </small>
                    </React.Fragment>
                  )}
                </StyledLabel>
                <Input
                  id="block-code"
                  name="blockCode"
                  value={blockCode || ''}
                  onChange={setBlockCode}
                  autoComplete="off"
                  readOnly={!!activeBlock}
                />
              </LargestCol> */}
              <LargestCol xs="12" lg={isAdmin ? '4' : '6'}>
                <StyledLabel htmlFor="input-account-number">
                  Rewards Account Number
                </StyledLabel>
                <Input
                  id="input-account-number"
                  name="rewardsAccountNumber"
                  value={accountNumber}
                  onChange={this.handleAccountNumberChanged}
                  invalid={accountNumberInvalid}
                  autoComplete="off"
                  disabled={isAdmin ? false : disabled}
                />
                <StyledPopover
                  placement="top-start"
                  isOpen={accountNumberInvalid && showValidationErrors}
                  target="input-account-number"
                  className="errors"
                  close={this.hideValidationErrors}
                  trigger="focus"
                >
                  <PopoverBody>
                    <Button
                      className="close"
                      onClick={this.hideValidationErrors}
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                    <p>Rewards account number must be 13 digits.</p>
                  </PopoverBody>
                </StyledPopover>
              </LargestCol>
              {/* TODO: Uncomment the second account number field after audit reports are done. */}
              {/*{isAdmin && isHostBooking && (*/}
              {/*  <LargestCol xs="12" lg="4">*/}
              {/*    <StyledLabel>Second Account Number</StyledLabel>*/}
              {/*    <Input*/}
              {/*      id="input-second-account-number"*/}
              {/*      name="secondAccountNumber"*/}
              {/*      value={secondAccountNumber}*/}
              {/*      onChange={this.handleSecondAccountNumberChanged}*/}
              {/*      invalid={secondAccountNumberInvalid}*/}
              {/*      autoComplete="off"*/}
              {/*      disabled={isAdmin ? false : disabled}*/}
              {/*    />*/}
              {/*  </LargestCol>*/}
              {/*)}*/}
              {!token && (
                <LargestCol xs="12" lg="2">
                  <StyledLabel required htmlFor="input-pin-number">
                    PIN
                  </StyledLabel>
                  {/* Note: we set autocomplete to "new-password" here to disable browser autofilling username/password */}
                  <Input
                    id="input-pin-number"
                    name="rewardsAccountPin"
                    type="password"
                    value={accountPin || ''}
                    onChange={this.handlePinChanged}
                    invalid={accountPinInvalid}
                    autoComplete="off"
                    disabled={disabled}
                  />
                  <StyledPopover
                    placement="bottom-start"
                    isOpen={accountPinInvalid && showValidationErrors}
                    target="input-pin-number"
                    className="errors"
                    close={this.hideValidationErrors}
                    trigger="focus"
                  >
                    <PopoverBody>
                      <Button
                        className="close"
                        onClick={this.hideValidationErrors}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                      <p>PIN must be 4 digits.</p>
                    </PopoverBody>
                  </StyledPopover>
                </LargestCol>
              )}
              <LargestCol xs="12" lg="2">
                <StyledButton
                  block
                  onClick={this.handleClick}
                  disabled={
                    (isAdmin && (!accountNumber && !blockCode)) ||
                    (!isAdmin && !accountPin && !blockCode) ||
                    loading ||
                    disabled
                  }
                >
                  <span className="d-md-none">Find Rates &amp; Offers </span>
                  <FontAwesomeIcon icon="sign-in-alt" />
                </StyledButton>
              </LargestCol>
            </Row>
          </Form>
        </StyledOffersForm>
      </React.Fragment>
    );
  }
}

OffersForm.propTypes = {
  setAccountNumber: PropTypes.func,
  setSecondAccountNumber: PropTypes.func,
  setAccountPin: PropTypes.func,
  accountNumber: PropTypes.string,
  accountPin: PropTypes.string,
  secondAccountNumber: PropTypes.string,
  submitRewardsAccount: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  isAdmin: PropTypes.bool,
  token: PropTypes.string,
  bookingSource: PropTypes.string,
  activeBlock: blockShape,
  submittedBlockCode: PropTypes.string,
  patronOrGroupCodeError: PropTypes.string,
  blockCode: PropTypes.string,
  setBlockCode: PropTypes.func.isRequired,
  resetBlockCode: PropTypes.func.isRequired,
  submitBlockCode: PropTypes.func.isRequired,
  inMobileApp: PropTypes.bool
};

export default OffersForm;
